"use client";

import { productHref } from "@/shared/routes";
import {
    Suspense,
    useEffect,
    useRef,
    useState,
    type ComponentProps,
    type MouseEvent as ReactMouseEvent,
    type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "next/navigation";
import { FiLogOut } from "react-icons/fi";
import {
    Header as UIHeader,
    type HeaderVariantKey,
    type DropdownOption,
} from "@dionatanbig2be/big2be-ui";
import { useAuthModal, useAuthStore, useFirstName } from "@/modules/auth";
import { useCartCount, useCartStore } from "@/modules/cart";
import { SearchSuggestions, useSearchController, useSearchFullscreen } from "@/modules/search";
import { useAppBoot, useAuthFormFields, useAvailableStores } from "../hooks/useBootstrap";
import { useHeaderCategoryPanel } from "@/modules/categories";
import { useStoreSwitcher } from "@/modules/store";
import { useConfirm } from "@/shared/components/useConfirm";
import { Icon } from "@/shared/components/Icon";
import { toast } from "@/shared/components/toast";
import { ACCOUNT_LINKS } from "@/modules/auth/lib/account-links";
import { useActiveStore } from "@/modules/store/stores/useActiveStore";
import { resolveMediaUrl } from "@/shared/helpers/media";
import { THEME_SCOPE_NAMES } from "@dionatanbig2be/big2be-ui/core";
import { useTheme } from "@/theme";
import { useHeaderData } from "../hooks/useHeaderData";
import { HeaderNotifications } from "./HeaderNotifications";
import { RecentOrdersDrawer } from "@/modules/profile";
// Card de hover "Última compra" desligado por enquanto — o botão fixo
// "Comprar de novo" (`cart.repeatOrder`) assumiu esse papel. Ver `cart` abaixo.
// import { HeaderCartHoverCard } from "./HeaderCartHoverCard";

interface HeaderProps {
    /** Header reduzido a logo + localização + conta (usado em checkout etc). */
    simplified?: boolean;
    /** Conteúdo extra do modo simplified (ex.: selo de compra segura do checkout). */
    simplifiedSlot?: ReactNode;
}

const TEMPLATE_TO_VARIANT: Record<string, HeaderVariantKey> = {
    variant1: "variant-1",
    variant2: "variant-2",
    variant3: "variant-3",
};

/**
 * Adapter do Header do big2be-ui: a variante vem do tema do cliente e o wiring
 * (carrinho, conta, loja, categorias, busca) chega na lib como callbacks.
 *
 * O Suspense existe porque `useSearchParams` (semeia o campo com `?q=`) força
 * bailout de CSR — isolado no `SearchParamsSeed` para não contaminar a página.
 */
export function Header(props: HeaderProps = {}) {
    return (
        <Suspense fallback={<HeaderContent {...props} initialQuery="" />}>
            <SearchParamsSeed {...props} />
        </Suspense>
    );
}

function SearchParamsSeed(props: HeaderProps) {
    const initialQuery = useSearchParams().get("q") ?? "";
    return <HeaderContent {...props} initialQuery={initialQuery} />;
}

function HeaderContent({
    simplified,
    simplifiedSlot,
    initialQuery,
}: HeaderProps & { initialQuery: string }) {
    const themeStructure = useTheme()?.structure;
    const headerStructure = themeStructure?.header;
    const template = headerStructure?.template ?? "variant1";
    const variant = TEMPLATE_TO_VARIANT[template] ?? "variant-1";
    // Campo novo em `structure.header`, ainda sem valor no back: o default `true`
    // preserva o comportamento hardcoded até o portal gravá-lo.
    const inverted = headerStructure?.inverted ?? false;

    // Onde o botão de categorias aparece — configurável pelo portal
    // (highlights.categoriesTrigger). Sem valor salvo, mantém o comportamento
    // atual (botão na barra de categorias, não no header).
    const categoriesTrigger = themeStructure?.highlights?.categoriesTrigger ?? "navbar";
    const departmentsLabel = themeStructure?.highlights?.departmentsLabel;
    const departmentsIcon = themeStructure?.highlights?.departmentsIcon;
    const { logoPath, logoAlt } = useHeaderData();

    // Só o desktop usa este controller (slot `search.dropdown`). No mobile o
    // `onOpen` abre o modal fullscreen, que monta a própria instância.
    const {
        value: query,
        setValue: setQuery,
        showDropdown,
        suggestions,
        results,
        isSearching,
        history,
        removeHistory,
        visitedProducts,
        handleChange,
        submit,
        close: closeDropdown,
        router,
    } = useSearchController({ initialValue: initialQuery });

    const wrapperRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        function onOutside(e: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node))
                closeDropdown();
        }
        document.addEventListener("mousedown", onOutside);
        return () => document.removeEventListener("mousedown", onOutside);
    }, [closeDropdown]);

    const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null);
    useEffect(() => {
        setScrollContainer((wrapperRef.current?.nextElementSibling as HTMLElement) ?? null);
    }, []);

    const cartCount = useCartCount();
    const openCart = useCartStore((s) => s.open);
    const user = useAuthStore((s) => s.user);
    const logout = useAuthStore((s) => s.logout);
    const firstName = useFirstName();
    const openAuth = useAuthModal((s) => s.openModal);
    const openCategoryPanel = useHeaderCategoryPanel((s) => s.openPanel);
    const ensureCategoriesLoaded = useHeaderCategoryPanel((s) => s.ensureLoaded);
    const openSearchFullscreen = useSearchFullscreen((s) => s.openModal);
    const openStoreSwitcher = useStoreSwitcher((s) => s.openModal);
    const storeId = useActiveStore((s) => s.storeId);
    const stores = useAvailableStores();
    const currentStore = storeId ? stores.find((s) => s.store_id === storeId) : undefined;
    // Regra do app_boot: sem `can_change_store` o header NÃO recebe `store` e a
    // lib não renderiza botão nenhum. Antes a prop era obrigatória e o gatilho
    // sobrava como rótulo inerte da loja fixa; antes disso ele abria o modal, o
    // usuário "salvava" e o SSR ignorava o cookie: recarregava sem mudar nada.
    const canChangeStore = useAppBoot()?.can_change_store ?? false;
    const registrationBlocked = useAuthFormFields().register.blocked;
    const [recentOrdersOpen, setRecentOrdersOpen] = useState(false);
    // Portal só depois do 1º clique (nunca no SSR/hidratação) e daí em diante
    // fica montado — o SideDrawer precisa disso para animar a saída.
    const [recentOrdersMounted, setRecentOrdersMounted] = useState(false);
    const confirm = useConfirm();

    // O histórico é da conta: deslogado, o "Comprar de novo" pede login.
    function openRecentOrders() {
        if (!user) {
            openAuth("login");
            return;
        }
        setRecentOrdersMounted(true);
        setRecentOrdersOpen(true);
    }

    async function handleLogout() {
        const ok = await confirm({
            title: "Sair da conta",
            message: "Tem certeza que deseja encerrar sua sessão?",
            confirmText: "Sair",
            tone: "danger",
        });
        if (!ok) return;
        try {
            await logout();
            toast("Logout realizado com sucesso.");
        } catch {
            toast.error("Não foi possível sair. Tente novamente.");
        }
    }

    const accountMenuOptions: DropdownOption[] = [
        ...ACCOUNT_LINKS.map(({ href, label, Icon }) => ({
            label,
            href,
            startAdornment: <Icon className="h-4 w-4" />,
            // href + push do MESMO valor é intencional: o Dropdown da lib
            // renderiza <a> cru (não next/link) — o preventDefault+push mantém
            // a navegação SPA sem perder o href (a11y, abrir em nova aba).
            onSelect: (e: ReactMouseEvent<HTMLAnchorElement>) => {
                e.preventDefault();
                router.push(href);
            },
        })),
        { separator: true },
        {
            label: "Sair da conta",
            tone: "error" as const,
            startAdornment: <FiLogOut className="h-4 w-4" />,
            onSelect: () => void handleLogout(),
        },
    ];

    // PONTE (até publicar a lib): a barra do header é pintada com a primária,
    // mas a versão publicada não declara a cor de TEXTO junto — o slot herdava
    // o `base-content` do tema escopado e o selo saía preto sobre o amarelo da
    // marca, em vez do `primary-content` que o cliente configurou. O par certo
    // é aplicado aqui, único ponto do app que conhece o `inverted`. Com a lib
    // nova (que já pareia fundo + texto), este wrapper vira redundância.
    const simplifiedSlotNode = simplifiedSlot ? (
        <span className={inverted ? "text-base-content" : "text-primary-content"}>
            {simplifiedSlot}
        </span>
    ) : undefined;

    return (
        // Tema escopado do widget: re-escopa os tokens daisyUI só nesta subárvore.
        <div ref={wrapperRef} data-theme={THEME_SCOPE_NAMES.header}>
            <UIHeader
                inverted={inverted}
                variant={variant}
                simplified={simplified}
                notificationSlot={!simplified ? <HeaderNotifications /> : undefined}
                {...({ simplifiedSlot: simplifiedSlotNode } as Record<string, unknown>)}
                scrollContainer={scrollContainer}
                logo={{ src: resolveMediaUrl(logoPath) ?? null, alt: logoAlt, href: "/" }}
                search={{
                    placeholder: "O que está procurando?",
                    value: query,
                    onChange: (e) => handleChange(e.target.value),
                    onSubmit: (e) => {
                        e.preventDefault();
                        submit(query);
                    },
                    dropdown: showDropdown ? (
                        <SearchSuggestions
                            query={query}
                            suggestions={suggestions}
                            searchResults={results}
                            isSearching={isSearching}
                            searchHistory={history}
                            visitedProducts={visitedProducts}
                            onSelectTerm={(term) => {
                                setQuery(term);
                                submit(term);
                            }}
                            onRemoveHistory={removeHistory}
                            onSelectVisited={(p) => {
                                closeDropdown();
                                router.push(productHref({ name: p.name, productId: Number(p.id) }));
                            }}
                            // Sem isto o CTA "Pergunte ao assistente" não aparece.
                            onAskChat={closeDropdown}
                        />
                    ) : null,
                    // Mobile: o campo vira gatilho readOnly e abre o modal fullscreen.
                    onOpen: () => openSearchFullscreen({ variant, inverted }),
                }}
                categories={
                    categoriesTrigger === "search"
                        ? {
                              label: departmentsLabel || "Categorias",
                              icon: departmentsIcon ? (
                                  <Icon name={departmentsIcon} size={16} />
                              ) : undefined,
                              onClick: () => {
                                  ensureCategoriesLoaded();
                                  openCategoryPanel();
                              },
                          }
                        : undefined
                }
                account={{
                    authenticated: Boolean(user),
                    label: firstName ? `Olá, ${firstName}` : undefined,
                    unauthenticatedLabel: registrationBlocked ? "Entre" : undefined,
                    onClick: () => openAuth("login"),
                    menu: user
                        ? {
                              options: accountMenuOptions,
                              sheetTitle: firstName ? `Olá, ${firstName}` : "Minha conta",
                          }
                        : undefined,
                }}
                store={
                    canChangeStore
                        ? {
                              label: currentStore?.description ?? "Escolher loja",
                              onClick: () => openStoreSwitcher(),
                          }
                        : undefined
                }
                // PONTE (até publicar a lib): `repeatOrder` só existe no
                // `HeaderCartProps` da big2be-ui nova — o cast segura o build
                // com a versão instalada. Publicada, o cast sai.
                cart={{
                    count: cartCount,
                    onClick: openCart,
                    // Botão fixo antes do carrinho, sempre visível (logado ou
                    // não — deslogado pede login). Abre as últimas compras
                    // para escolher qual refazer. Fora do checkout
                    // (`simplified`), onde refazer pedido só atrapalharia.
                    repeatOrder: simplified
                        ? undefined
                        : { label: "Comprar de novo", onClick: openRecentOrders },
                    // Hover no carrinho (desktop): "Refazer última compra". Desligado
                    // por enquanto em favor do botão fixo acima — para religar,
                    // descomente esta linha e o import do HeaderCartHoverCard.
                    // hoverCard: user ? <HeaderCartHoverCard /> : undefined,
                } as ComponentProps<typeof UIHeader>["cart"]}
            />
            {/* No body, não aqui: dentro do wrapper a gaveta herdaria o tema
                escopado do header (e o `nextElementSibling` acima mudaria). */}
            {recentOrdersMounted &&
                !simplified &&
                user &&
                createPortal(
                    <RecentOrdersDrawer open={recentOrdersOpen} onClose={() => setRecentOrdersOpen(false)} />,
                    document.body
                )}
        </div>
    );
}
